// Shared saved-accounts utility
// All account persistence logic lives here to avoid duplication.
// Refresh tokens are encrypted via AES-GCM before storage.

import { encryptToken, decryptToken } from './token-crypto';

export interface SavedAccount {
  id: string;
  email?: string;
  phone?: string;
  provider: string;
  display_name: string;
  avatar_url: string | null;
  last_login: string;
  refresh_token?: string; // encrypted when stored
}

const STORAGE_KEY = 'mycue_saved_accounts';
const MAX_ACCOUNTS = 5;

export const getSavedAccounts = (): SavedAccount[] => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

/**
 * Get a saved account with its refresh_token decrypted.
 */
export const getDecryptedAccount = async (account: SavedAccount): Promise<SavedAccount> => {
  if (!account.refresh_token) return account;
  const decrypted = await decryptToken(account.refresh_token);
  return { ...account, refresh_token: decrypted };
};

export const removeSavedAccount = (id: string) => {
  const updated = getSavedAccounts().filter(a => a.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
};

/**
 * Upsert the current user's account entry.
 * The refresh_token is encrypted before being stored.
 */
export const upsertSavedAccount = async (account: Omit<SavedAccount, 'last_login'>) => {
  const accounts = getSavedAccounts().filter(a => a.id !== account.id);
  const encryptedToken = account.refresh_token
    ? await encryptToken(account.refresh_token)
    : undefined;
  accounts.unshift({
    ...account,
    refresh_token: encryptedToken,
    last_login: new Date().toISOString(),
  });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(accounts.slice(0, MAX_ACCOUNTS)));
};

/**
 * Update ONLY the refresh_token for an existing account (by user id).
 * The token is encrypted before storage.
 */
export const updateRefreshToken = async (userId: string, refreshToken: string) => {
  const accounts = getSavedAccounts();
  const idx = accounts.findIndex(a => a.id === userId);
  if (idx === -1) return;
  accounts[idx].refresh_token = await encryptToken(refreshToken);
  accounts[idx].last_login = new Date().toISOString();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(accounts));
};
